import type { View } from "@novorender/api";
import type { BoundingSphere, HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import type { Predicate } from "@procore/bim-index";
import type { ReadonlyVec3 } from "gl-matrix";
import pMap from "p-map";

// Deep imports rather than the `apis/procore` barrel, which pulls in the RTK Query API
// and the analytics client (and through them the store) for two endpoint modules — the
// same reason `pickDataAccess` is kept out of the `dataAccess` barrel.
import {
    BIM_QUERY_BOUNDS_ID_LIMIT,
    BIM_QUERY_EXPAND_ID_LIMIT,
    BIM_QUERY_OBJECT_PAGE_LIMIT,
    BIM_QUERY_RESOLVE_ID_LIMIT,
    BIM_QUERY_ROARING_ID_LIMIT,
    BIM_QUERY_ROOT,
    type BimQueryBoundsParts,
    type BimQueryClimbPolicy,
    type BimQueryIdSet,
    type BimQueryObject,
    type BimQueryRoaringIds,
    type BimQueryTarget,
    climbBimQueryIdSet,
    climbBimQueryObjects,
    collapseBimQueryIdSet,
    expandBimQueryIdSet,
    filteredBimQueryTree,
    getBimQueryBounds,
    getBimQueryObject,
    listAllBimQueryChildren,
    listBimQueryDescendantIdSet,
    resolveBimQueryInstanceIds,
    resolveBimQueryObjectIds,
    resolveBimQuerySelectionIdSet,
    searchBimQueryIdSet,
    searchBimQueryObjects,
} from "apis/procore/bimQuery";
import type { SearchPredicate } from "apis/procore/searchPredicate";
import {
    type BoundsIds,
    type BoundsOptions,
    type BoundsParts,
    type CollapsedIds,
    type ExpandIdsOptions,
    type FilteredTree,
    type FilteredTreeObject,
    type FilteredTreeOptions,
    type FindIdsOptions,
    type FindOptions,
    objectBoundsAsSelection,
    type ObjectDataAccess,
    ObjectMetadataCache,
    type ObjectVisibilityStats,
    type ObjectVisibilityStatsRequest,
    oneObjectAsListing,
    type PropertyQuery,
    type PropertyTerm,
    resolveSelectionLegacy,
    type SelectionBounds,
    type SelectionResolutionMode,
    type SelectionResolutionOptions,
    type SelectionTarget,
    singleIdPropertyQuery,
    singleSelectionTarget,
} from "dataAccess";
import { NodeType, type PublicId } from "types/misc";
import { log } from "utils/log";
import { mergeBoundingSpheres, unionAABB3 } from "utils/math";
import { isLeafNode } from "utils/objectPath";

const l = log.dataAccess.bimIndex;

import { expandIdsByIdQuery, extractAnyOfIdTermFromQuery, extractAnyOfPublicIdTermFromQuery } from "dataAccess/utils";

import { DescendantCache } from "./descendantCache";
import { isInstanceId, isInternalObjectId, translateIds } from "./idTranslation";
import {
    ancestorPaths,
    bulkChildrenPredicate,
    childrenPredicate,
    idsPredicate,
    objectNodeClassPredicate,
    pathsPredicate,
    queryPredicate,
} from "./predicate";
import { MAX_PREDICATE_VALUES, restPredicate } from "./restPredicate";
import { bimIndexBitmapIds, bimIndexIdsBitmap, bimIndexSerializedIds } from "./session";
import { readObjectVisibilityStats, type VisibilityStatsIndex } from "./visibilityStats";

const EMPTY_ID_ARRAY = new Uint32Array();

/**
 * How many reads one search or expansion may take, resumptions included, before this stops asking.
 *
 * A cut-short scan hands back a token that continues it, and continuing can itself be
 * cut short — so the honest read is a loop. The bound is what keeps a scene whose
 * scans never complete from looping forever: past it the set is reported incomplete,
 * which is what it would have been anyway, instead of never being reported at all.
 */
const MAX_RESUMED_READS = 10;

/**
 * How many resolved clicks are remembered.
 *
 * An entry count rather than the byte budget `ObjectMetadataCache` needs: class-aware
 * resolution climbs to an *object*, whose subtree is bounded in a way a file's or a
 * container's is not, so the ids an entry holds cannot run away the way
 * {@link SelectionTarget.highlightIds} warns a highlight set in general can.
 */
const MAX_SELECTION_RESOLUTION_ENTRIES = 100;

/** How many batches of one split id-list read (climb or expansion) are in flight at once. */
const BATCH_READ_CONCURRENCY = 5;

/** The degradation a scan cut off by the engine's time budget reports. */
const SCAN_TIMEOUT = "SCAN_TIMEOUT_EXCEEDED";

/** How many narrower requests a timed-out expansion batch is re-asked as. */
const SCAN_TIMEOUT_SPLIT_PARTS = 4;

/** The narrowest batch a timed-out expansion is split into; one this narrow keeps its degraded answer. */
const SCAN_TIMEOUT_MIN_BATCH = 1000;

async function* emptyIterator(): AsyncIterableIterator<HierarcicalObjectReference> {}

/**
 * A resolved click and whether the index narrowed anything answering it — which is what
 * says the answer is safe to remember, and so never leaves this module.
 */
type SelectionResolution = { target: SelectionTarget; degraded: boolean };

/**
 * A resolution taken as complete. Legacy resolution reads its subtree through
 * {@link ObjectDataAccess.getDescendantIds}, which reports a narrowed read to the log
 * rather than to its caller, so there is nothing to carry here.
 */
function complete(target: SelectionTarget | undefined): SelectionResolution | undefined {
    return target && { target, degraded: false };
}

/** Why a read narrowed, as a log suffix — the bitmap reads name their causes. */
function causes(set: Pick<BimQueryIdSet, "degradations">): string {
    return set.degradations.length ? ` (${set.degradations.join(", ")})` : "";
}

/**
 * Degradation kinds whose answer is still every match the request named. The engine's own
 * word for the rest is `isIncomplete`, which the HTTP surface does not carry — `Bim-Degraded`
 * is the wider `isDegraded`, and it fires on these too.
 *
 * Listed as the exceptions rather than the other way round so an unrecognized kind — a new
 * one, or one this list has no ruling on — counts a read short. An omitted figure is the
 * cheaper mistake.
 *
 * `MATCH_LIMIT_REACHED` and `VALUE_LIST_TRUNCATED` are *not* here, though the engine calls
 * them complete: that ruling is for a caller that asked to be cut off, and these reads ask
 * for the whole set.
 *
 * @see `WasmQueryOutcome.isIncomplete` and `QueryHint` in `@procore/bim-index`.
 */
const EXACT_DEGRADATIONS: ReadonlySet<string> = new Set([
    "UNIVERSE_SCAN",
    "PATTERN_SCAN_NOT_ACCELERATED",
    "CASE_SENSITIVE_PATTERN_FALLBACK",
    // An input instance id naming nothing in this version. Its other meaning, a row an
    // `id_kind=external` answer cannot name, cannot arise: bitmaps are internal ids.
    "UNRESOLVED_EXTERNAL_IDS",
]);

/**
 * Bitmaps whose union is one answer, and whether that answer is every match the request
 * named — for the callers that need a whole set rather than as much of one as arrived.
 */
type IdSetRead = { sets: BimQueryIdSet[]; whole: boolean };

/** Reads that answer one request between them, as one. */
function joinedReads(reads: readonly IdSetRead[]): IdSetRead {
    return { sets: reads.flatMap((read) => read.sets), whole: reads.every((read) => read.whole) };
}

/** A read that lost matches, as against one the engine merely had to work for. */
function cameUpShort(set: BimQueryIdSet): boolean {
    return set.degradations.some((kind) => !EXACT_DEGRADATIONS.has(kind));
}

/** `roots` as internal ids, `undefined` for `Procore/InstanceId`s (strings, no bitmap form). */
function internalIdRoots(roots: readonly number[] | readonly string[] | Uint32Array): Uint32Array | undefined {
    if (roots instanceof Uint32Array) {
        return roots;
    }
    return typeof roots[0] === "number" ? Uint32Array.from(roots as readonly number[]) : undefined;
}

/** `roots` in {@link expandedIdSets}' request widths, still as ids. */
function expansionBatches(
    roots: readonly number[] | readonly string[] | Uint32Array,
): (Uint32Array | readonly string[])[] {
    const internal = internalIdRoots(roots);
    return internal
        ? idBatches(internal, BIM_QUERY_ROARING_ID_LIMIT)
        : idBatches(roots as readonly string[], BIM_QUERY_EXPAND_ID_LIMIT);
}

/** `ids` cut into runs of at most `width`; the array itself when it fits, since slicing would copy it. */
function idBatches<T extends Uint32Array | readonly string[]>(ids: T, width: number): T[] {
    if (ids.length <= width) {
        return [ids];
    }
    const batches: T[] = [];
    for (let from = 0; from < ids.length; from += width) {
        batches.push(ids.slice(from, from + width) as T);
    }
    return batches;
}

/**
 * A timed-out batch as the narrower ones to re-ask it as: up to
 * {@link SCAN_TIMEOUT_SPLIT_PARTS}, none under {@link SCAN_TIMEOUT_MIN_BATCH}. Empty when
 * the batch cannot be split that way, which leaves its degraded answer standing.
 */
function scanTimeoutSplit<T extends Uint32Array | readonly string[]>(batch: T): T[] {
    const parts = Math.min(SCAN_TIMEOUT_SPLIT_PARTS, Math.floor(batch.length / SCAN_TIMEOUT_MIN_BATCH));
    return parts < 2 ? [] : idBatches(batch, Math.ceil(batch.length / parts));
}

/** The API spells its enums SCREAMING_SNAKE_CASE; the interface spells them lowercase. */
const WIRE_BOUNDS_PARTS: Record<BoundsParts, BimQueryBoundsParts> = {
    box: "BOX",
    sphere: "SPHERE",
    both: "BOTH",
};

/**
 * The two resolution modes are the two climb policies, under the names each side uses:
 * the shallowest matching ancestor is the "first object", the deepest is the "last".
 */
const CLIMB_POLICIES: Record<SelectionResolutionMode, BimQueryClimbPolicy> = {
    "first-object": "TOPMOST",
    "last-object": "NEAREST",
};

/**
 * The API's `has_children` as the viewer's node type: a node with children is a grouping
 * row, and one without is a leaf. Every row and every object the API answers with carries
 * the flag, so this is the only place the two vocabularies meet.
 */
function nodeType(hasChildren: boolean): ObjectData["type"] {
    return hasChildren ? NodeType.Internal : NodeType.Leaf;
}

/**
 * An index object as `ObjectData`. Read-only, as everything on the Procore backend is.
 *
 * The node type comes off the object's own `has_children`, so an object read is as
 * informative about it as a tree row is — see {@link nodeType}.
 */
function toObjectData(object: BimQueryObject): ObjectData {
    const data: ObjectData = {
        id: object.id,
        name: object.name,
        path: object.path,
        type: nodeType(object.hasChildren),
        properties: object.properties,
        bounds: object.bounds,
        async loadMetaData() {
            return data;
        },
        async save() {
            return false;
        },
    };
    return data;
}

/**
 * The same query, split so that no single term is wider than the API will plan.
 *
 * Callers legitimately ask about a whole selection — `predicate.ts` spends hundreds of
 * thousands of ids as an `OR` of `IN` chunks for exactly that — but a served index has a
 * branch-width ceiling a local one does not (see {@link MAX_PREDICATE_VALUES}), so past
 * it the query has to become several requests. Splitting the *query* rather than the
 * predicate keeps `predicate.ts` the only place that knows how a term becomes a
 * predicate.
 *
 * Recursive, so a query with two over-wide terms is covered rather than half-covered:
 * the union over every combination of batches is the original conjunction.
 */
function splitWideTerms(query: PropertyQuery): PropertyQuery[] {
    const wide = query.terms.findIndex((term) => (term.anyOf?.length ?? 0) > MAX_PREDICATE_VALUES);
    if (wide === -1) {
        return [query];
    }
    const values = query.terms[wide]!.anyOf!;
    const batches: PropertyQuery[] = [];
    for (let from = 0; from < values.length; from += MAX_PREDICATE_VALUES) {
        const batch = values.slice(from, from + MAX_PREDICATE_VALUES);
        // Rebuilt rather than spread over: `PropertyTerm` is a union of exactly one
        // match kind, and only the `anyOf` kind can be over-wide.
        const terms = query.terms.map((term, at): PropertyTerm =>
            at === wide ? { property: term.property, negate: term.negate, anyOf: batch } : term,
        );
        batches.push(...splitWideTerms({ terms }));
    }
    return batches;
}

/**
 * Data access backed by a BIM index **served over the network** — Procore's BIM Query
 * API (`apis/procore/bimQuery`).
 *
 * The sibling `BimIndexOfflineDataAccess` anticipated: the same index, the same query
 * algebra and the same `predicate.ts` translation, but answered by a service that has
 * the files mounted rather than by a worker reading OPFS. That makes it available to
 * every scene rather than only a synced one, which is why it sits between the downloaded
 * index and the object DB in `pickDataAccess`.
 *
 * It is a separate implementation from the offline one because the two differ in exactly
 * what the interface asks about: `isIndexAvailableOffline` is unconditionally `true`
 * there and `false` here. Nothing else about the answers differs.
 *
 * Object *properties* come from the scene's own metadata (`view.data`) when the scene
 * carries any, and from the index's own object read otherwise — unlike the offline
 * backend, which stops at the scene because reaching for a server would defeat its
 * purpose. One consequence of the API's row shapes is worth knowing rather than
 * discovering: its **tree** rows — `children` and `ancestors` — carry a name and nothing
 * else, no path and no bounds, where a row this app hands back wants both. So the
 * hierarchy is read through `objects/search` over the index's `$path` and `$depth`
 * columns instead ({@link getChildren}, {@link getAncestors}, {@link getParent}), whose
 * object rows carry path, bounds and `has_children` at `view=LIGHT`.
 *
 * No request is made at construction, so choosing this backend at startup costs nothing
 * until it is asked something.
 */
export class BimIndexDataAccess implements ObjectDataAccess {
    readonly kind = "bim-index" as const;

    private readonly metadataCache = new ObjectMetadataCache();

    /**
     * Subtree ids for this session, and what `object.descendants` reads through. Held
     * here rather than as a `WeakMap` keyed by the backend because there is exactly one
     * backend per scene, so the cache's lifetime *is* the scene's.
     */
    private readonly descendants = new DescendantCache();

    /** Iteration order is LRU order: entries are re-inserted on access. */
    private readonly selectionResolutionCache = new Map<string, SelectionTarget>();

    constructor(
        private readonly view: View,
        private readonly target: BimQueryTarget,
    ) {}

    isIndexAvailableOffline() {
        return false;
    }

    isDataAvailableOffline() {
        return Boolean(this.view.data);
    }

    /**
     * The scene's own metadata when it has any, otherwise one object read from the index.
     *
     * A bare id is enough for the node type too: the object read answers `has_children`,
     * so this reports the same `Internal`/`Leaf` a tree row would for the same id.
     */
    getObject(id: ObjectId, signal?: AbortSignal): Promise<ObjectData | undefined> {
        return this.object(id, signal);
    }

    /**
     * The first object at `path`, found by an equality query on the index's `$path`.
     *
     * One read: the page is asked for at `view=FULL`, so its row arrives with the
     * properties an object read would have gone back for. Affordable here where it is not
     * for {@link matches} because the page is one row wide. The object goes into the
     * metadata cache on the way past, as {@link indexObject}'s own reads do.
     */
    async getObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined> {
        const predicate = pathsPredicate([path]);
        if (!predicate) {
            return undefined;
        }
        const page = await this.unlessAborted(signal, () =>
            searchBimQueryObjects(this.target, {
                predicate: restPredicate(predicate),
                page: 1,
                perPage: 1,
                view: "FULL",
                signal,
            }),
        );
        const first = page?.items[0];
        if (!first) {
            return undefined;
        }
        const object = toObjectData(first);
        this.metadataCache.put(object.id, object);
        return object;
    }

    /**
     * The object at the path the given one hangs off, for {@link getAncestors}' reason —
     * the last prefix of a path is its parent's ({@link ancestorPaths}), so this is the
     * same `$path` lookup {@link getObjectAtPath} makes, over one level.
     *
     * A path with no prefix belongs to a top-level node, which has no parent to read.
     *
     * One request where the `ancestors` endpoint takes two: its rows carry a name and an
     * id alone, leaving the object behind the head to be read afterwards.
     */
    async getParent(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData | undefined> {
        const parentPath = ancestorPaths(object.path).at(-1);
        return parentPath === undefined ? undefined : this.getObjectAtPath(parentPath, signal);
    }

    /**
     * A node's children, or the top-level nodes when given none — as a search over the
     * node's own path, not the `children` endpoint.
     *
     * A search because a row needs more than that endpoint answers: its tree rows carry a
     * name alone, where the tree draws a **path** and reads **bounds** off the row for its
     * own fly-to. Search rows carry both, so a listing costs one request per page either
     * way rather than a path composed here and a bounds read per child.
     *
     * See {@link childrenPredicate} for how "one level below this node" is asked for — the
     * same predicate the offline backend sends its own engine. Rows arrive in id order
     * rather than the tree's, which is the caller's to sort: the model tree already orders
     * siblings by path. Paged as the iterator is consumed, so a caller that stops early
     * stops the requests.
     */
    getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        return this.matches(restPredicate(childrenPredicate(parent?.path)), { signal });
    }

    /**
     * One OR-predicate across all parent paths. {@link bulkChildrenPredicate} builds the
     * same per-parent shape as {@link childrenPredicate}, so the result is identical to
     * calling {@link getChildren} per parent and merging.
     */
    findChildrenOf(
        parents: readonly string[],
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        if (!parents.length) {
            return (async function* () {})();
        }
        // Paged as the caller pulls, same as getChildren. matches requests the next page
        // only after the caller takes the last row of this one, and breaking out of the
        // iteration closes the generator, so that request never happens.
        // loadFrontierInBulk breaks once a parent reaches MAX_TOTAL_CHILDREN_PER_NODE.
        return this.matches(restPredicate(bulkChildrenPredicate(parents)), { signal });
    }

    /**
     * One predicate over the query's terms, paged as the iterator is consumed.
     *
     * A lone `id` term is read instead: {@link getObject} answers from the metadata
     * cache when the object has already been seen, where a predicate is a search
     * request for one row no matter how often the same id is asked for. That read is an
     * object rather than a row, so it satisfies {@link FindOptions.full} whether or not
     * it was asked for.
     */
    findByProperties(query: PropertyQuery, options?: FindOptions): AsyncIterableIterator<HierarcicalObjectReference> {
        if (!query.terms.length) {
            return emptyIterator();
        }
        const id = singleIdPropertyQuery(query);
        return id !== undefined
            ? oneObjectAsListing(this.getObject(id, options?.signal))
            : this.matchesOf(query, options);
    }

    async findIdsByProperties(query: PropertyQuery, options?: FindIdsOptions): Promise<Uint32Array> {
        const { includeDescendants, requireWhole, signal } = options ?? {};
        if (!query.terms.length) {
            return EMPTY_ID_ARRAY;
        }
        const assertWhole = (read: IdSetRead, name: string) => {
            if (requireWhole && !read.whole) {
                throw new Error(`the ${name} came up short`);
            }
        };

        // Single search when no descendants needed
        if (!includeDescendants) {
            const matches = await this.matchIdSets(query, signal);
            if (!matches?.sets.length || signal?.aborted) {
                return EMPTY_ID_ARRAY;
            }
            assertWhole(matches, "search");

            const roots = await bimIndexBitmapIds(matches.sets.map((set) => set.bytes));
            return signal?.aborted ? EMPTY_ID_ARRAY : roots;
        }

        // If query is anyOf with publicId/internalId - no need to search, can expand directly
        let roots: readonly string[] | readonly number[] | Uint32Array | undefined =
            extractAnyOfIdTermFromQuery(query) ?? extractAnyOfPublicIdTermFromQuery(query);

        // If not, search the index first
        if (!roots) {
            const matches = await this.matchIdSets(query, signal);
            if (!matches?.sets.length || signal?.aborted) {
                return EMPTY_ID_ARRAY;
            }
            assertWhole(matches, "search");

            roots = await bimIndexBitmapIds(matches.sets.map((set) => set.bytes));
            if (signal?.aborted) {
                return EMPTY_ID_ARRAY;
            }
        }

        if (!roots?.length) {
            return EMPTY_ID_ARRAY;
        }

        // `include_self` rather than unioning the roots in afterwards: the endpoint
        // answers with every id it was given, tree node or not, and the roots are not
        // always internal ids there is something to union them into.
        const expanded = await this.expandedIdSets(roots, { signal, includeSelf: true });
        if (!expanded || signal?.aborted) {
            return EMPTY_ID_ARRAY;
        }
        assertWhole(expanded, "expansion");

        const ids = await bimIndexBitmapIds(expanded.sets.map((set) => set.bytes));
        return signal?.aborted ? EMPTY_ID_ARRAY : ids;
    }

    /**
     * One `objects/filtered_tree` read per batch of {@link splitWideTerms}, each spending what
     * is left of the match budget. An incomplete read is warned about rather than resumed.
     *
     * A row the service could build no path for is left out: the tree is keyed by path.
     */
    async getFilteredTree(query: PropertyQuery, { maxResults, signal }: FilteredTreeOptions): Promise<FilteredTree> {
        const empty: FilteredTree = { objects: [], truncated: false };
        const byId = new Map<ObjectId, FilteredTreeObject>();
        let matchCount = 0;
        let truncated = false;

        for (const batch of query.terms.length ? splitWideTerms(query) : []) {
            const predicate = queryPredicate(batch);
            if (!predicate) {
                continue;
            }
            if (matchCount >= maxResults) {
                truncated = true;
                break;
            }
            const result = await this.unlessAborted(signal, () =>
                filteredBimQueryTree(this.target, {
                    predicate: restPredicate(predicate),
                    maxResults: Number.isFinite(maxResults) ? maxResults - matchCount : undefined,
                    signal,
                }),
            );
            if (!result) {
                return empty;
            }
            if (result.incomplete) {
                l.warn("incomplete filtered tree; matches may be missing");
            }
            truncated ||= result.truncated || result.incomplete;

            for (const { path, isMatch, ...node } of result.nodes) {
                if (path === null) {
                    continue;
                }
                const known = byId.get(node.id);
                if (isMatch && !known?.isMatch) {
                    matchCount++;
                }
                if (known) {
                    known.isMatch ||= isMatch;
                } else {
                    byId.set(node.id, Object.assign(this.reference({ ...node, path }), { isMatch }));
                }
            }
        }

        return signal?.aborted ? empty : { objects: [...byId.values()], truncated };
    }

    /** One `id` expansion for every target the references cannot answer themselves. */
    expandIds(
        targets: readonly (ObjectId | HierarcicalObjectReference)[],
        options?: ExpandIdsOptions,
    ): Promise<Uint32Array> {
        return expandIdsByIdQuery(this, targets, options);
    }

    /**
     * The union of these roots' subtrees, still as bitmaps. Internal ids go as roaring
     * bitmaps; instance ids as {@link BIM_QUERY_EXPAND_ID_LIMIT}-wide arrays.
     *
     * @returns `undefined` for an abandoned read.
     * @param roots Internal ids as numbers, `Procore/InstanceId`s as strings — the
     *   encoding {@link expandBimQueryIdSet} reads from the element type.
     * @param options.includeSelf Include the roots themselves. Defaults to the endpoint's
     *   own `true`.
     */
    private async expandedIdSets(
        roots: readonly number[] | readonly string[] | Uint32Array,
        { signal, includeSelf }: { signal?: AbortSignal; includeSelf?: boolean } = {},
    ): Promise<IdSetRead | undefined> {
        if (!roots.length) {
            return { sets: [], whole: true };
        }
        const expanded = await pMap(
            expansionBatches(roots),
            (batch) => this.expandedBatch(batch, { signal, includeSelf }),
            { concurrency: BATCH_READ_CONCURRENCY, signal },
        ).catch((e) => {
            // `pMap` rejects when its signal fires, where this answers an abort with
            // `undefined`. Anything else is a real failure and still throws.
            if (signal?.aborted) {
                return undefined;
            }
            throw e;
        });
        if (!expanded || expanded.some((read) => !read)) {
            return undefined;
        }
        return joinedReads(expanded as IdSetRead[]);
    }

    /**
     * One expansion request, resumed while the service offers a token. One that times out
     * with no token to continue from is re-asked as up to {@link SCAN_TIMEOUT_SPLIT_PARTS}
     * narrower ones. The parts run one at a time, inside the slot their batch already
     * holds: a scan that timed out is not answered by more concurrent scans.
     *
     * @returns `undefined` for an abandoned read.
     */
    private async expandedBatch(
        batch: Uint32Array | readonly string[],
        { signal, includeSelf }: { signal?: AbortSignal; includeSelf?: boolean },
    ): Promise<IdSetRead | undefined> {
        const ids =
            batch instanceof Uint32Array
                ? await this.unlessAborted(signal, () => bimIndexIdsBitmap(batch)).then(
                      (roaring) => roaring && { roaring },
                  )
                : batch;
        if (!ids) {
            return undefined;
        }
        const read = await this.resumedIdSets(
            "expansion",
            (resumeToken) => expandBimQueryIdSet(this.target, { ids, includeSelf, resumeToken, signal }),
            signal,
        );
        if (!read) {
            return undefined;
        }
        const last = read.sets.at(-1)!;
        const parts =
            last.resumeToken === undefined && last.degradations.includes(SCAN_TIMEOUT) ? scanTimeoutSplit(batch) : [];
        if (!parts.length) {
            return read;
        }
        l.info(`expansion of ${batch.length} ids timed out; retrying as ${parts.length} batches`);
        const partReads: IdSetRead[] = [];
        for (const part of parts) {
            const partRead = await this.expandedBatch(part, { signal, includeSelf });
            if (!partRead) {
                return undefined;
            }
            partReads.push(partRead);
        }
        return joinedReads(partReads);
    }

    /** `ids` as roaring bitmaps, one per {@link BIM_QUERY_ROARING_ID_LIMIT}. `undefined` for an abandoned read. */
    private async roaringBatches(ids: Uint32Array, signal?: AbortSignal): Promise<BimQueryRoaringIds[] | undefined> {
        const batches: BimQueryRoaringIds[] = [];
        for (let from = 0; from < ids.length; from += BIM_QUERY_ROARING_ID_LIMIT) {
            // The array itself when it fits: slicing would copy it once more.
            const batch =
                ids.length <= BIM_QUERY_ROARING_ID_LIMIT ? ids : ids.slice(from, from + BIM_QUERY_ROARING_ID_LIMIT);
            const roaring = await this.unlessAborted(signal, () => bimIndexIdsBitmap(batch));
            if (!roaring) {
                return undefined;
            }
            batches.push({ roaring });
        }
        return batches;
    }

    /**
     * The sets as they came, with a narrowed one reported.
     *
     * @param read The read's name, for the log line — `"climb"`.
     */
    private reportedIdSets(sets: readonly BimQueryIdSet[], read: string): BimQueryIdSet[] {
        for (const set of sets) {
            if (set.degraded) {
                // Ids returned are correct; the set covers fewer objects than the request
                // names, with nothing to name what is missing.
                l.warn(`degraded ${read}; the set may be incomplete${causes(set)}`);
            }
        }
        return [...sets];
    }

    /**
     * `objects/resolve` — the index's own bulk mapping, which is this method exactly.
     *
     * Batched at {@link BIM_QUERY_RESOLVE_ID_LIMIT}, and only the ids the endpoint will
     * read are sent (see `idTranslation`).
     */
    resolveObjectIdsToInstanceIds(objectIds: ObjectId[], signal?: AbortSignal): Promise<(string | null)[] | undefined> {
        return translateIds(objectIds, isInternalObjectId, (readable) =>
            this.batchedTranslation(readable, signal, (batch) =>
                resolveBimQueryInstanceIds(this.target, { objectIds: batch, signal }),
            ),
        );
    }

    /** The same endpoint the other way round — see {@link resolveObjectIdsToInstanceIds}. */
    resolveInstanceIdsToObjectIds(
        instanceIds: string[],
        signal?: AbortSignal,
    ): Promise<(ObjectId | null)[] | undefined> {
        return translateIds(instanceIds, isInstanceId, (readable) =>
            this.batchedTranslation(readable, signal, (batch) =>
                resolveBimQueryObjectIds(this.target, { instanceIds: batch, signal }),
            ),
        );
    }

    /**
     * `objects/collapse`, with the ids sent as one bitmap. A failure other than the abort,
     * or a degradation outside {@link EXACT_DEGRADATIONS} and `KIND_UNAVAILABLE`, answers
     * every id as an object: collapsing only shortens an answer.
     */
    async collapseIds(ids: Uint32Array, signal?: AbortSignal): Promise<CollapsedIds> {
        const empty = { subtreeIds: EMPTY_ID_ARRAY, objectIds: EMPTY_ID_ARRAY };
        if (!ids.length) {
            return empty;
        }

        try {
            const bytes = await bimIndexSerializedIds(ids);
            const sets = await this.unlessAborted(signal, () =>
                collapseBimQueryIdSet(this.target, { ids: bytes, signal }),
            );
            if (!sets) {
                return empty;
            }
            if (sets.degraded) {
                // A short answer cannot be repaired on apply: what it lost is in neither
                // set. `degraded` with no kinds named is not known to be coarse only.
                const coarseOnly = sets.degradations.every(
                    (kind) => kind === "KIND_UNAVAILABLE" || EXACT_DEGRADATIONS.has(kind),
                );
                if (!sets.degradations.length || !coarseOnly) {
                    l.warn(`degraded collapse; keeping every id as an object${causes(sets)}`);
                    return { subtreeIds: EMPTY_ID_ARRAY, objectIds: ids };
                }
                l.warn(`degraded collapse; the answer may be coarser than needed${causes(sets)}`);
            }

            const [subtreeIds, objectIds] = await Promise.all([
                bimIndexBitmapIds([sets.subtrees]),
                bimIndexBitmapIds([sets.objects]),
            ]);
            return signal?.aborted ? empty : { subtreeIds, objectIds };
        } catch (e) {
            if (signal?.aborted) {
                return empty;
            }
            l.warn("collapse failed; keeping every id as an object", e);
            return { subtreeIds: EMPTY_ID_ARRAY, objectIds: ids };
        }
    }

    /**
     * One id translation as however many requests its width takes, concatenated.
     *
     * Sound because the answer is positional and each batch answers its own slice of it in
     * order — the endpoint is not paged, so batching is the client's own and invisible in
     * the result.
     *
     * Which is only true while each batch answers its whole slice: a short one shifts every
     * later batch's answers onto the wrong ids, and the concatenation would hide it. So a
     * batch answered at any other width makes the mapping unavailable, exactly as an
     * aborted one does.
     */
    private async batchedTranslation<T, R>(
        ids: T[],
        signal: AbortSignal | undefined,
        translate: (batch: T[]) => Promise<(R | null)[]>,
    ): Promise<(R | null)[] | undefined> {
        const translated: (R | null)[] = [];
        for (let from = 0; from < ids.length; from += BIM_QUERY_RESOLVE_ID_LIMIT) {
            const batch = ids.slice(from, from + BIM_QUERY_RESOLVE_ID_LIMIT);
            const answers = await this.unlessAborted(signal, () => translate(batch));
            if (!answers || answers.length !== batch.length) {
                return undefined;
            }
            translated.push(...answers);
        }
        return translated;
    }

    /**
     * The chain in one request, root-first.
     *
     * A search for {@link getChildren}'s reason: the ancestor rows that endpoint answers
     * carry a name alone, where a breadcrumb row wants the **path** it stands for and the
     * **bounds** a fly-to from it reads. {@link getParent} asks for the last of these
     * prefixes the same way.
     *
     * The pieces are the prefixes of the object's path, which *are* its ancestors' paths
     * ({@link ancestorPaths}), so the whole chain is one `$path IN` — the same equality
     * lookup {@link getObjectAtPath} makes, over every level at once.
     *
     * The order is imposed here rather than taken from the response: rows come back in id
     * order, and each is placed by the prefix that asked for it, which is already
     * root-first. A prefix no object sits at is left out rather than guessed, which is the
     * gap the interface tells callers to cope with.
     */
    async getAncestors(
        object: HierarcicalObjectReference,
        signal?: AbortSignal,
    ): Promise<HierarcicalObjectReference[]> {
        const paths = ancestorPaths(object.path);
        const predicate = pathsPredicate(paths);
        if (!predicate) {
            return [];
        }
        const rows = new Map<string, HierarcicalObjectReference>();
        for await (const row of this.matches(restPredicate(predicate), { signal })) {
            rows.set(row.path, row);
        }
        if (signal?.aborted) {
            // A chain cut short is not a shorter chain: the interface answers an abort
            // with its empty value.
            return [];
        }
        return paths.map((path) => rows.get(path)).filter((row): row is HierarcicalObjectReference => Boolean(row));
    }

    /**
     * The whole subtree, kept in {@link DescendantCache} and read back off the object
     * through it.
     *
     * `object.descendants` becomes an accessor over the cache rather than the ids
     * themselves: `treeHelpers` counts them against the selection *synchronously* to
     * render a collapsed folder's checkbox, and the cache can answer that from a shrunk
     * set without a worker. Assigning the expanded array instead would pin up to MBs per
     * folder to a reference the tree recreates constantly, with nothing bounding the
     * total — which is what a warm-up over thousands of nodes would do.
     *
     * Cached there rather than in {@link warmDescendantIds} because a read made for *any*
     * reason — a user hiding a folder, selection resolution climbing a subtree, a
     * background warm-up — then serves every later one.
     *
     * The ids are ascending, since a bitmap decodes in order, which is what
     * `countSelectedDescendants`' binary search needs.
     */
    async getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array> {
        if (signal?.aborted) {
            return EMPTY_ID_ARRAY;
        }
        const own = object.descendants;
        if (own) {
            return own instanceof Uint32Array ? own : new Uint32Array(own);
        }
        // `isLeafNode` rather than the node type: a container file is often stamped
        // `Leaf` and still owns a subtree (BM-10439), and the objects reaching here can
        // come from scene metadata rather than from a row this backend typed itself.
        if (isLeafNode(object)) {
            return EMPTY_ID_ARRAY;
        }
        const ids = await this.descendants.getOrLoad(object, signal, async (shared) => {
            const set = await this.unlessAborted(shared, () =>
                listBimQueryDescendantIdSet(this.target, { id: object.id, signal: shared }),
            );
            if (!set) {
                // Abandoned by every asker. The interface answers an abort with the empty
                // value rather than with however much of the subtree had arrived.
                return undefined;
            }
            if (set.degraded) {
                l.warn(`degraded subtree read; some descendants may be missing${causes(set)}`);
            }
            // An empty body is an empty set, and is not one: an empty roaring set
            // serializes to 8 bytes, so no bytes at all is not something WASM will
            // deserialize — see the worker's `bitmapIds`. The buffer is the decoder's to
            // transfer; the cache stores the ids, not what they arrived as.
            const ids = set.bytes.length ? await bimIndexBitmapIds([set.bytes]) : EMPTY_ID_ARRAY;
            return { ids, complete: !set.degraded };
        });
        if (signal?.aborted) {
            return EMPTY_ID_ARRAY;
        }
        // A read that was not stored — degraded, or abandoned — leaves the accessor
        // answering `undefined`, which is what "the ids are not on the object" means to
        // `warmDescendantIds` and to `treeHelpers`.
        this.descendants.defineLazyDescendants(object);
        return ids;
    }

    /**
     * A {@link getDescendantIds} read for its side effect — the ids on the object — and
     * nothing else. Which node still needs one is the caller's business, but asking twice
     * costs nothing: a node that already has them is answered without a request.
     *
     * Unlike the object DB there is no pre-computed file to hit or miss, so "cheap" here
     * means one request and one bitmap decode rather than free. That is the trade this
     * backend takes, because the alternative is a collapsed folder whose checkbox cannot
     * know its own subtree. **The cost is per node, and a warm-up runs over many** —
     * callers bound it: `fetchDescendantsBatch` caps concurrency, and its own callers cap
     * how many nodes one run covers.
     *
     * Reports whether the ids are now on the object, which is the only thing a caller can
     * act on. So a `false` is not "no descendants" — an empty subtree is warmed and reports
     * `true`. It means the read was aborted or failed, which is left to the next attempt
     * rather than failing the batch around it.
     */
    async warmDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<boolean> {
        try {
            await this.getDescendantIds(object, signal);
        } catch (e) {
            l.warn(`could not warm the subtree of object ${object.id}`, e);
            return false;
        }
        return Boolean(object.descendants);
    }

    /**
     * `objects/bounds` — one request for the whole selection, folded by the service out
     * of the index's `$bounds/*` columns rather than out of rows this would otherwise
     * have to read one at a time.
     *
     * Ids are internal or external per {@link ObjectDataAccess.getBounds}'s `ids`, sent
     * through untranslated either way — `getBimQueryBounds` names the encoding by JSON
     * type — so a caller holding public ids never pays a separate resolve request first.
     * The fast paths below (single id, metadata cache) are keyed on internal ids, so they
     * only apply to an `internalIds` batch; a `publicIds` one skips straight to the
     * request.
     *
     * Batched only because the request body is, and folding the batches is sound: a
     * volume enclosing each part encloses the whole. The sphere fold is the same
     * approximation `mergeBoundingSpheres` makes everywhere else, so batching can widen
     * the radius slightly — invisible at a camera, and the batch size is far above any
     * real selection anyway.
     */
    async getBounds(ids: BoundsIds, options?: BoundsOptions): Promise<SelectionBounds | undefined> {
        const { parts = "sphere", signal } = options ?? {};
        const internal = ids.internalIds !== undefined;
        const idArr: ObjectId[] | PublicId[] = internal ? Array.from(ids.internalIds) : Array.from(ids.publicIds);
        if (!idArr.length) {
            return undefined;
        }

        if (internal && idArr.length === 1) {
            const obj = await this.getObject(idArr[0] as ObjectId, signal);
            return objectBoundsAsSelection(obj?.bounds, parts);
        }

        const spheres: BoundingSphere[] = [];
        const boxes: { min: ReadonlyVec3; max: ReadonlyVec3 }[] = [];

        const combineResult = () => {
            const bounds: SelectionBounds = {};
            const sphere = mergeBoundingSpheres(spheres);
            if (sphere) {
                bounds.sphere = sphere;
            }
            const box = unionAABB3(boxes);
            if (box) {
                bounds.box = box;
            }
            return bounds.sphere || bounds.box ? bounds : undefined;
        };

        const allObjectsCached =
            internal &&
            idArr.length < ObjectMetadataCache.MAX_ENTRIES &&
            (idArr as ObjectId[]).every((id) => this.metadataCache.has(id));

        if (allObjectsCached) {
            // Take bounds from cache if all the objects are cached already.
            // No need to batch the promises because they are in flight or resolved already.
            try {
                for (const id of idArr as ObjectId[]) {
                    const obj = await this.indexObject(id);
                    if (obj?.bounds) {
                        if (obj.bounds.sphere && parts !== "box") {
                            spheres.push(obj.bounds.sphere);
                        }
                        if (obj.bounds.box && parts !== "sphere") {
                            boxes.push(obj.bounds.box);
                        }
                    }
                }
                return combineResult();
            } catch (e) {
                l.error("error getting bounds from cache", e);
            }
        }

        for (let from = 0; from < idArr.length; from += BIM_QUERY_BOUNDS_ID_LIMIT) {
            const result = await this.unlessAborted(signal, () =>
                getBimQueryBounds(this.target, {
                    ids: idArr.slice(from, from + BIM_QUERY_BOUNDS_ID_LIMIT),
                    parts: WIRE_BOUNDS_PARTS[parts],
                    signal,
                }),
            );
            if (!result) {
                return undefined;
            }
            if (result.degraded) {
                // Unlike a degraded search, this cannot have narrowed the volume: the
                // engine answers no bounds rather than partial ones. It says which ids it
                // could not place, which is worth knowing when a camera lands short.
                l.warn("degraded bounds; some of the selection may not have been included");
            }
            if (result.bounds?.sphere) {
                spheres.push(result.bounds.sphere);
            }
            if (result.bounds?.box) {
                boxes.push(result.bounds.box);
            }
        }

        return combineResult();
    }

    /**
     * What a click selects, memoised per picked id — a click and the right-click that
     * opens its context menu resolve the same id, as does every re-click on an object the
     * user is working with.
     *
     * Keyed on the mode and on `classAware` as well as the id: the two modes climb to
     * different ancestors for the same click, and the two resolutions answer with
     * different objects entirely, so an entry written under one must never answer for
     * the other.
     *
     * A degraded answer is not remembered. Its highlight set is a partial subtree, and
     * an entry for it would keep handing that partial set to every later click on the
     * same id — geometry that stays unhighlighted until the entry is evicted.
     *
     * The entry is handed out as-is rather than copied. Sound because {@link SelectionTarget}
     * is read-only by contract — the highlight reducer takes its ids as an `Iterable` and
     * builds its own set — and because `mainObject` is the same shared `ObjectData` the
     * metadata cache already hands to every other caller.
     */
    async resolveSelection(id: ObjectId, options: SelectionResolutionOptions): Promise<SelectionTarget | undefined> {
        const { mode, classAware, signal } = options;

        // Answered without reading anything, so an entry for it would only evict one that
        // cost a request.
        if (!classAware && mode === "last-object") {
            return singleSelectionTarget(id);
        }

        if (signal?.aborted) {
            // Checked before the lookup rather than only around the requests: a hit would
            // otherwise answer a superseded click where a miss answers `undefined`.
            return undefined;
        }

        const key = `${mode}:${classAware ? "class" : "legacy"}:${id}`;
        const cached = this.selectionResolutionCache.get(key);
        if (cached) {
            this.selectionResolutionCache.delete(key);
            this.selectionResolutionCache.set(key, cached);
            return cached;
        }

        const resolution = classAware
            ? await this.resolveSelectionByNodeClass(id, mode, signal)
            : await this.resolveSelectionWithoutNodeClass(id, mode, signal).then(complete);
        if (!resolution) {
            // An abort, or an id the index cannot place. Neither is an answer, and the
            // interface does not distinguish them — so neither is remembered.
            return undefined;
        }
        if (!resolution.degraded) {
            this.rememberSelectionResolution(key, resolution.target);
        }
        return resolution.target;
    }

    /** @see readObjectVisibilityStats */
    getObjectVisibilityStats(request: ObjectVisibilityStatsRequest): Promise<ObjectVisibilityStats> {
        return readObjectVisibilityStats(this.statsIndex(), request);
    }

    /** The reads `visibilityStats` makes, over this API. */
    private statsIndex(): VisibilityStatsIndex {
        return {
            topLevelFiles: async (signal) => {
                // A short level would make the scene smaller than it is: the files left out
                // never get a chance to show, and in `isolated` the total they feed is low.
                const nodes = await this.unlessAborted(signal, () =>
                    listAllBimQueryChildren(this.target, { id: BIM_QUERY_ROOT, signal }),
                );
                // A top-level node's path is its name.
                return nodes?.map((node) => ({ ...node, path: node.name }));
            },
            matchIdsWithDescendants: async (predicate, signal) => {
                const matched = await this.matchIdSetsWithPredicate(predicate, signal);
                // A short set would leave the stats reporting a confident undercount of
                // what the planes cut, where a search refuses only the figure it feeds.
                if (!matched?.whole) {
                    return undefined;
                }
                // Decoded so the matches can be returned apart from their descendants.
                const matches = await bimIndexBitmapIds(matched.sets.map((set) => set.bytes));
                if (signal?.aborted) {
                    return undefined;
                }
                if (!matches.length) {
                    return { matches, descendants: EMPTY_ID_ARRAY };
                }
                // `includeSelf: false`: the matches are already in hand.
                const expanded = await this.expandedIdSets(matches, { signal, includeSelf: false });
                if (!expanded?.whole) {
                    return undefined;
                }
                const descendants = await bimIndexBitmapIds(expanded.sets.map((set) => set.bytes));
                return signal?.aborted ? undefined : { matches, descendants };
            },
            filesHolding: async (files, ids, signal) => {
                // One climb: the topmost ancestors of the set are the files holding it.
                const predicate = idsPredicate(files.map((file) => file.id));
                if (!predicate || !ids.size) {
                    return new Set();
                }
                const climbed = await this.climbToIdSets(ids.sortedIds, restPredicate(predicate), signal);
                if (!climbed) {
                    return undefined;
                }
                const holding = await bimIndexBitmapIds(climbed.map((set) => set.bytes));
                return signal?.aborted ? undefined : new Set(holding);
            },
            getDescendantIds: (object, signal) => this.getDescendantIds(object, signal),
        };
    }

    /**
     * Nothing of its own to release. There is no index open here — the session worker this
     * borrows for bitmap decoding holds no files and is shared, so closing it would only
     * cost the next consumer a restart.
     */
    async dispose() {}

    private rememberSelectionResolution(key: string, target: SelectionTarget) {
        this.selectionResolutionCache.set(key, target);
        if (this.selectionResolutionCache.size > MAX_SELECTION_RESOLUTION_ENTRIES) {
            const oldest = this.selectionResolutionCache.keys().next();
            if (!oldest.done) {
                this.selectionResolutionCache.delete(oldest.value);
            }
        }
    }

    /**
     * `climb` and `selection`, **concurrently** — one round trip for the whole answer.
     *
     * Both take the same body and both are keyed on the picked id, so neither waits on the
     * other: `climb` answers *which* object (`view=FULL`, so its properties come too), and
     * `selection` answers that same climb expanded over the object's subtree, as a bitmap.
     * Composing `expand` on top of `climb` here would mean paging the ancestor out just to
     * send it back in.
     *
     * The resolved object goes into the metadata cache on the way past — the panels that
     * open on a new selection read it next.
     *
     * A climb landing on nothing falls back to legacy, so class-aware never resolves *less*
     * than pre-`NodeClass` would have; the two spent requests are the price of not
     * serializing the common case.
     */
    private async resolveSelectionByNodeClass(
        id: ObjectId,
        mode: SelectionResolutionMode,
        signal?: AbortSignal,
    ): Promise<SelectionResolution | undefined> {
        const request = {
            ids: [id],
            targetPredicate: restPredicate(objectNodeClassPredicate()),
            // A picked node that is itself an object answers for itself rather than
            // climbing past to whichever object encloses it.
            includeSelf: true,
            policy: CLIMB_POLICIES[mode],
            signal,
        } as const;

        const [climbed, resolved] = await Promise.all([
            // One input, so the union is one ancestor — a page of one is the whole answer.
            this.unlessAborted(signal, () => climbBimQueryObjects(this.target, { ...request, perPage: 1 })),
            this.unlessAborted(signal, () => resolveBimQuerySelectionIdSet(this.target, request)),
        ]);
        if (!climbed || !resolved) {
            return undefined;
        }

        const target = climbed.items[0];
        if (!target) {
            // A cut-short climb may have missed the object it was meant to land on, so the
            // fallback carries its degradation rather than passing for a clean answer.
            const fallback = await this.resolveSelectionWithoutNodeClass(id, mode, signal);
            return fallback && { target: fallback, degraded: climbed.degraded };
        }
        const degraded = climbed.degraded || resolved.degraded;
        if (degraded) {
            // The object is right; the highlight set may cover less of its subtree than it
            // stands for, which shows up as geometry that stays unhighlighted.
            l.warn(`degraded selection resolution for ${id}; the highlight set may be incomplete${causes(resolved)}`);
        }

        const object = toObjectData(target);
        this.metadataCache.put(object.id, object);

        // The resolved object unioned in rather than trusted to be in the bitmap: `expand`
        // contributes nothing for an id that owns no tree node, and a selection missing the
        // object it selected highlights nothing where an object-DB click highlights one id.
        const highlightIds = await bimIndexBitmapIds([resolved.bytes], new Uint32Array([object.id]));
        return signal?.aborted
            ? undefined
            : {
                  target: { mainObjectId: object.id, mainObject: object, highlightIds, outcome: "resolved" },
                  degraded,
              };
    }

    /** Legacy resolution, for a flag-off session and for a chain with no object in it. */
    private async resolveSelectionWithoutNodeClass(
        id: ObjectId,
        mode: SelectionResolutionMode,
        signal?: AbortSignal,
    ): Promise<SelectionTarget | undefined> {
        if (mode === "last-object") {
            return singleSelectionTarget(id);
        }
        const clicked = await this.getObject(id, signal);
        if (!clicked || signal?.aborted) {
            return undefined;
        }
        return resolveSelectionLegacy({ access: this, clicked, mode, signal });
    }

    /**
     * Every bitmap the query's match set takes: one per term batch, plus one per scan
     * the engine resumed. `undefined` for an abort, which the caller answers with the
     * interface's empty value.
     */
    private async matchIdSets(query: PropertyQuery, signal?: AbortSignal): Promise<IdSetRead | undefined> {
        const batches = splitWideTerms(query);
        if (batches.length > 1) {
            l.info(`a term wider than ${MAX_PREDICATE_VALUES} values was split across ${batches.length} requests`);
        }

        const reads: IdSetRead[] = [];
        for (const batch of batches) {
            const predicate = queryPredicate(batch);
            if (!predicate) {
                continue;
            }
            const read = await this.matchIdSetsWithPredicate(predicate, signal);
            if (!read) {
                return undefined;
            }
            reads.push(read);
        }
        return joinedReads(reads);
    }

    private matchIdSetsWithPredicate(predicate: Predicate, signal?: AbortSignal): Promise<IdSetRead | undefined> {
        return this.resumedIdSets(
            "search",
            (resumeToken) =>
                searchBimQueryIdSet(this.target, { predicate: restPredicate(predicate), resumeToken, signal }),
            signal,
        );
    }

    /**
     * One bitmap read, resumed for as long as each answer hands back a token — up to
     * {@link MAX_RESUMED_READS}. `undefined` for an abort.
     *
     * A resumed read is not a page — it *widens* the set with matches the previous read
     * never reached — so its answer is another whole set to union.
     *
     * Whole when the last read lost nothing: a loss on a read that handed back a token is
     * what the next read recovers. The offline worker's `matchedIds` rules the same way.
     *
     * @param name The read's name, for the log line — `"search"`, `"expansion"`.
     * @param read The request, given the token to resume from.
     */
    private async resumedIdSets(
        name: string,
        read: (resumeToken: string | undefined) => Promise<BimQueryIdSet>,
        signal?: AbortSignal,
    ): Promise<IdSetRead | undefined> {
        const sets: BimQueryIdSet[] = [];
        let resumeToken: string | undefined;
        for (let reads = 1; ; reads++) {
            const set = await this.unlessAborted(signal, () => read(resumeToken));
            if (!set) {
                return undefined;
            }
            sets.push(set);
            resumeToken = set.resumeToken;
            if (!resumeToken) {
                if (set.degraded) {
                    // Nothing left to continue with: the set is short for good.
                    l.warn(`degraded ${name}; results may be incomplete${causes(set)}`);
                }
                return { sets, whole: !cameUpShort(set) };
            }
            if (reads >= MAX_RESUMED_READS) {
                l.warn(
                    `${name} still incomplete after ${reads} resumed reads; results may be incomplete${causes(set)}`,
                );
                return { sets, whole: false };
            }
        }
    }

    /**
     * The ancestors matching `targetPredicate` over `ids` of any width, still as bitmaps —
     * {@link expandedIdSets}'s shape, and for the same reason: the caller decides what to
     * union them with, and nothing is decoded on the way through.
     *
     * The bitmap representation rather than the paged one, which is offset-paged and answers
     * a whole `view=FULL` object per ancestor where only the ids are wanted.
     *
     * @returns `undefined` for an abandoned read.
     */
    private async climbToIdSets(
        ids: Uint32Array,
        targetPredicate: SearchPredicate,
        signal?: AbortSignal,
    ): Promise<BimQueryIdSet[] | undefined> {
        if (!ids.length) {
            return [];
        }
        const batches = await this.roaringBatches(ids, signal);
        if (!batches) {
            return undefined;
        }
        const climbed = await pMap(
            batches,
            (batch) =>
                this.unlessAborted(signal, () =>
                    climbBimQueryIdSet(this.target, {
                        ids: batch,
                        targetPredicate,
                        policy: "TOPMOST",
                        includeSelf: true,
                        signal,
                    }),
                ),
            { concurrency: BATCH_READ_CONCURRENCY, signal },
        ).catch((e) => {
            // `pMap` rejects when its signal fires, where this answers an abort with
            // `undefined`. Anything else is a real failure and still throws.
            if (signal?.aborted) {
                return undefined;
            }
            throw e;
        });
        if (!climbed || climbed.some((set) => !set)) {
            return undefined;
        }
        const sets = this.reportedIdSets(climbed as BimQueryIdSet[], "climb");
        // A short union is worse than none: a file missing from it reads as holding no
        // excluded id, which is the answer "wholly visible". Each batch stands alone here
        // rather than continuing the one before it, so every one of them has to be whole.
        return sets.some((set) => cameUpShort(set) || set.resumeToken !== undefined) ? undefined : sets;
    }

    /** Every batch of a split query, in order, with overlapping rows yielded once. */
    private async *matchesOf(
        query: PropertyQuery,
        options?: FindOptions,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        const batches = splitWideTerms(query);
        if (batches.length > 1) {
            l.info(`a term wider than ${MAX_PREDICATE_VALUES} values was split across ${batches.length} requests`);
        }
        // One batch is the common case and needs no bookkeeping. Several can overlap,
        // since a multi-valued property can put one object in two batches, and a caller
        // computing over the result must not see it twice.
        const seen = batches.length > 1 ? new Set<ObjectId>() : undefined;

        for (const batch of batches) {
            const predicate = queryPredicate(batch);
            if (!predicate) {
                continue;
            }
            for await (const row of this.matches(restPredicate(predicate), options)) {
                if (seen) {
                    if (seen.has(row.id)) {
                        continue;
                    }
                    seen.add(row.id);
                }
                yield row;
            }
        }
    }

    /**
     * Rows for a predicate, paged as the iterator is consumed.
     *
     * `full` asks each page for `view=FULL` and hands the rows straight back as their own
     * metadata, so `loadMetaData()` resolves to what the page already carried. They are
     * deliberately *not* seeded into {@link metadataCache}: a page is a thousand rows
     * wide, and a broad search would evict the entries the per-id reads live on — which is
     * the opposite of what a caller iterating a match set once needs.
     */
    private async *matches(
        predicate: SearchPredicate,
        options?: FindOptions,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        const { full, signal } = options ?? {};
        for (let page = 1; ; page++) {
            const result = await this.unlessAborted(signal, () =>
                searchBimQueryObjects(this.target, {
                    predicate,
                    page,
                    perPage: BIM_QUERY_OBJECT_PAGE_LIMIT,
                    view: full ? "FULL" : "LIGHT",
                    signal,
                }),
            );
            if (!result) {
                return;
            }
            if (result.degraded) {
                // The engine says the read may have covered less than it was asked for.
                // The rows are correct; rows are missing, with nothing to name them.
                l.warn("degraded search; results may be incomplete");
            }
            for (const object of result.items) {
                yield full ? toObjectData(object) : this.reference(object);
            }
            if (result.items.length < BIM_QUERY_OBJECT_PAGE_LIMIT) {
                return;
            }
        }
    }

    /** One object, from the scene when it has it and from the index otherwise. */
    private async object(id: ObjectId, signal?: AbortSignal): Promise<ObjectData | undefined> {
        try {
            const fromScene = (await this.view.data?.getObjectMetaData(id)) as ObjectData | undefined;
            if (fromScene) {
                return fromScene;
            }
        } catch {
            // A scene that cannot answer for this id is not a failure — the index may
            // still know it, which is the whole reason this backend has a second source.
        }
        return this.unlessAborted(signal, () => this.indexObject(id));
    }

    /**
     * One object read from the index, memoised per id — the read every panel that opens
     * on a selection makes, and the one this backend repeats most.
     *
     * The read is not given the caller's signal: the entry is shared, so cancelling it
     * for one caller would reject the object for everyone else waiting on the same
     * promise, and a single object GET is not worth that. `AppObjectDB`'s cache over the
     * object DB is the same shape for the same reason.
     */
    private indexObject(id: ObjectId): Promise<ObjectData | undefined> {
        return this.metadataCache.getOrLoadMetadata(id, async () => {
            const object = await getBimQueryObject(this.target, { id });
            return object && toObjectData(object);
        });
    }

    /**
     * A search row as the rest of the app expects one — including the bounds a `LIGHT`
     * page carries, which is what the tree's own fly-to reads off a row. `loadMetaData`
     * reaches for the properties through the same {@link object} read — so a caller can
     * open any row without knowing where either half came from.
     */
    private reference({
        id,
        path,
        bounds,
        hasChildren,
    }: Pick<BimQueryObject, "id" | "path" | "bounds" | "hasChildren">): HierarcicalObjectReference {
        const loadObject = () => this.object(id);
        return {
            id,
            path,
            // `NodeType` in the API is an ambient const enum, which `isolatedModules`
            // cannot read; `types/misc` mirrors its values (see the note there).
            type: nodeType(hasChildren),
            ...(bounds ? { bounds } : {}),
            async loadMetaData(): Promise<ObjectData> {
                const object = await loadObject();
                if (!object) {
                    throw new Error(`Object ${id} has no metadata available`);
                }
                return object;
            },
        };
    }

    /**
     * The interface's abort contract, applied to a request.
     *
     * `ObjectDataAccess` promises that an aborted call answers with its own empty value
     * rather than rejecting — but a signal handed to `fetch` cancels the request *by*
     * rejecting it, and cancelling the request is the whole point of forwarding a signal
     * to a backend that makes them. So the rejection is turned back into the empty answer
     * here: `undefined` for the page or object the caller was waiting on, which each
     * method maps onto its own empty. Anything that is not the abort still throws.
     */
    private async unlessAborted<T>(signal: AbortSignal | undefined, work: () => Promise<T>): Promise<T | undefined> {
        if (signal?.aborted) {
            return undefined;
        }
        try {
            return await work();
        } catch (e) {
            if (signal?.aborted) {
                return undefined;
            }
            throw e;
        }
    }
}
